import React, { Component } from 'react';
import PropTypes from 'prop-types';
import cn from 'classnames';

import { SEARCH_IMGS_NOT_LAZY_LOADED } from 'constants/appConstants';
import { evSearchPageClickthrough } from 'events/search';

import styles from 'styles/components/search/products.scss';
import { onEvent } from 'helpers/EventHelpers';
import { Loader } from 'components/Loader';
import SearchInlineRecos from 'components/search/SearchInlineRecos';
import InlineBannerAd from 'components/search/InlineBannerAd';
import SearchInlineVerticalRecos from 'components/search/SearchInlineVerticalRecos';
import { track } from 'apis/amethyst';
import SearchFeedback from 'components/search/SearchFeedback';
import { withErrorBoundary } from 'components/common/MartyErrorBoundary';
import SponsoredProducts from 'components/search/SponsoredProducts';

export class Products extends Component {
  static displayName = 'Products';

  state = {
    relatedStyleOpenedIndex: null,
    // Active animation index for products that get detail zoom
    animationTimerIndex: 0
  }

  componentDidMount() {
    onEvent(window, 'click', this.handleDocClick, null, this);
    onEvent(window, 'keydown', this.handleKeydownClick, null, this);
    this.startAnimationTimer();
  }

  shouldComponentUpdate(nextProps, nextState) {
    const { products, layout, page, heartsData = {}, colorSwatchToggleState } = this.props;
    const { relatedStyleOpenedIndex, animationTimerIndex } = this.state;
    const { hearts } = heartsData;
    const { nextHeartsData = {} } = nextProps;
    const { nextHearts } = nextHeartsData;
    if (animationTimerIndex !== nextState.animationTimerIndex) {
      return true;
    }
    return nextProps.products !== products
      || nextProps.layout !== layout
      || nextProps.page !== page
      || nextHearts !== hearts
      || nextProps.colorSwatchToggleState !== colorSwatchToggleState
      || nextState.relatedStyleOpenedIndex !== relatedStyleOpenedIndex;
  }

  componentWillUnmount() {
    this.stopAnimationTimer();
  }

  // Set an initial interval ID that we can clear later when the component unmounts so we don't start two animation timers
  intervalID = 0

  // Start a global animation timer for any products that have detail zoom.  Having a top level timer keeps them in sync regardless
  // of re-renders or lazyloading, which will tick every 800ms
  startAnimationTimer = () => {
    const MAX_ANIMATION_FRAMES = 9;
    this.intervalID = setInterval(() => {
      let newIndex = this.state.animationTimerIndex + 1;

      // Reset the cycle
      if (newIndex > MAX_ANIMATION_FRAMES) {
        newIndex = 0;
      }
      this.setState({
        animationTimerIndex: newIndex
      });
    }, 800);
  }

  stopAnimationTimer = () => {
    clearInterval(this.intervalID);
  }

  handleDocClick = e => {
    const container = e.target.closest('[data-card-related-styles-container]');
    if (!container) {
      this.setState({ relatedStyleOpenedIndex: null });
    }
  }

  handleKeydownClick = e => {
    if (e.keyCode === 27) {
      this.setState({ relatedStyleOpenedIndex: null });
    }
  }

  getLayoutStyles(layout) {
    switch (layout) {
      case 'two-item':
        return 'searchTwoWideMobile';
      case 'three-item':
        return 'searchThreeWideMobile';
      case 'four-item':
        return 'searchFourWideMobile';
      default:
        return 'searchThreeWideMobile';
    }
  }

  setRelatedStyleOpenedIndex = index => this.setState({ relatedStyleOpenedIndex: index });

  makeProducts() {
    const {
      productClass,
      ProductComponent,
      filters: { page, term },
      products: { list, inlineVerticalRecos, inlineRecos },
      onProductClicked,
      layout,
      onInlineRecoClick,
      onInlineVerticalRecoClick,
      heartsData = {},
      msaImageParams,
      showRatingStars,
      hydraSearchColorSwatches,
      hydraSocialCollections,
      colorSwatchToggleState,
      sponsoredProducts,
      hydraAds,
      hydraBannerAds,
      isCustomer,
      hydraPDPProductAngles } = this.props;
    const {
      onHeartClick,
      hearts,
      heartsList = {}
    } = heartsData;
    const { testId } = this.context;
    const { animationTimerIndex } = this.state;
    if (list?.length > 0) {
      const layoutStyle = this.getLayoutStyles(layout);
      const verticalRecoCount = inlineVerticalRecos ? inlineVerticalRecos.length : 0;
      const horizontalRecoCount = inlineRecos && inlineRecos.recos ? inlineRecos.recos.length : 0;
      const additionalClasses = {
        container: `${styles.productContainer} ${layoutStyle}`
      };
      return (
        <div className={cn(styles.products, productClass || null)} data-vertical-recos={verticalRecoCount} data-horizontal-recos={horizontalRecoCount}>
          {verticalRecoCount > 0 && <h2 className={styles.resultsHeading}>All Results</h2>}
          {list.map((v, i) => (
            <ProductComponent
              key={`${v.styleId}-${v.colorId}`}
              componentStyle="searchProduct"
              onComponentClick={e => {
                const index = i + 1;
                onProductClicked(index, v.productId, v.styleId, e);
                track(evSearchPageClickthrough, {
                  pageNumber: page + 1,
                  pageResultNumber: index,
                  product: v,
                  numberOfHearts: heartsList[v.styleId],
                  swatchCount: v.relatedStyles?.length
                });
              }}
              heartsData={heartsData}
              additionalClasses={additionalClasses}
              animationTimerIndex={animationTimerIndex}
              cardData={v}
              index={i}
              msaImageParams={msaImageParams}
              melodyCardTestId={testId('searchResult')}
              showRatingStars={showRatingStars}
              shouldLazyLoad={i > SEARCH_IMGS_NOT_LAZY_LOADED}
              colorSwatchToggleState={colorSwatchToggleState}
              hydraPDPProductAngles={hydraPDPProductAngles}
              hydraSearchColorSwatches={hydraSearchColorSwatches}
              hydraSocialCollections={hydraSocialCollections}
              swatchClickCallback={(swatch, swatchIndex) => {
                const index = i + 1;
                track(evSearchPageClickthrough, {
                  pageNumber: page + 1,
                  pageResultNumber: index,
                  product: swatch,
                  numberOfHearts: heartsList[v.styleId],
                  swatchCount: v.relatedStyles?.length,
                  swatchIndex
                });
              }}
              isRelatedStyleDropdownOpen={this.state.relatedStyleOpenedIndex === i}
              setRelatedStyleOpenedIndex={this.setRelatedStyleOpenedIndex}/>
          ))}
          {!!sponsoredProducts?.styles && hydraAds && !isCustomer // Only display ads to those both in the AB test and unrecognized
            ?
            <SponsoredProducts
              sponsoredProducts={sponsoredProducts}
              onHeartClick={onHeartClick}
              msaImageParams={msaImageParams}
              hearts={hearts}
              showRatingStars={showRatingStars}
              heartsList={heartsList}
              heartsData={heartsData}
              searchTerm={term}
            />
            :
            !!horizontalRecoCount &&
            <SearchInlineRecos
              heartsData={heartsData}
              showHearting={heartsData.showFavoriteHeart}
              onHeartClick={onHeartClick}
              inlineRecos={inlineRecos}
              onInlineRecoClick={onInlineRecoClick}
              msaImageParams={msaImageParams}
              hearts={hearts}
              showRatingStars={showRatingStars}
              heartsList={heartsList}
              searchTerm={term}
            />}
          {!!verticalRecoCount &&
            <SearchInlineVerticalRecos
              showHearting={heartsData.showFavoriteHeart}
              onHeartClick={onHeartClick}
              inlineRecos={inlineVerticalRecos}
              onInlineVerticalRecoClick={onInlineVerticalRecoClick}
              msaImageParams={msaImageParams}
              hearts={hearts}
              showRatingStars={showRatingStars}
              heartsList={heartsList}
            />}
          {/* We only display the inline ad if there are enough products to have a 4th row at max products per row (5 x 3) */}
          {list.length > 15 && hydraBannerAds && <InlineBannerAd layoutStyle={layoutStyle} />}
        </div>
      );
    }
    return null;// no results view handled by NoSearchResults.js
  }

  render() {
    const { showLoader, products: { oosMessaging, isLoading }, handleSearchFeedbackClick, feedbackSubmitted } = this.props;
    const { testId, marketplace: { search: { showSearchFeedback } } } = this.context;
    return (
      <div ref="search" id="searchPage" className="searchPage">
        {!isLoading && showSearchFeedback && <SearchFeedback handleClick={handleSearchFeedbackClick} feedbackSubmitted={feedbackSubmitted} />}
        <h2>Search Results</h2>
        {oosMessaging && <p className={styles.oosMessaging} data-test-id={testId('oosMessage')}>{oosMessaging}</p>}
        {showLoader && isLoading ? <Loader /> : this.makeProducts()}
      </div>
    );
  }
}

Products.propTypes = {
  filters: PropTypes.object.isRequired,
  layout: PropTypes.string,
  onProductClicked: PropTypes.func.isRequired,
  page: PropTypes.number.isRequired,
  pageCount: PropTypes.number,
  products: PropTypes.object.isRequired,
  showRatings: PropTypes.bool.isRequired,
  onInlineRecoClick: PropTypes.func,
  msaImageParams: PropTypes.object.isRequired
};

Products.contextTypes = {
  testId: PropTypes.func,
  marketplace: PropTypes.object
};

export default withErrorBoundary('Products', Products);
